import type { GetApplicationResponseBody } from "services/onlineApplicationService";
import type { ContactData } from "store/application/applicationSlice";
import { formatPhone } from "utils/formatPhone";
import { toLowerCaseGenderFromSingleLetter } from "utils/conversions";

/**
 * Transforms the GetDTOApp API response into the ContactData structure used by Redux.
 * Handles field name mapping, type conversions, and optional field handling.
 * Note: dateOfBirth, state, hasUsedNicotine, and clubCode are not hydrated from backend - these come from quoteData.
 *
 * @param response - The response from the getApplication API call
 * @param applicantType - The type of applicant (member or spouse)
 * @returns ContactData structure ready to be dispatched to Redux store
 */
export const transformGetDTOAppToContactData = (
    response: GetApplicationResponseBody,
    applicantType: "member" | "spouse"
): ContactData => {
    // Transform contact information
    // Note: Only hydrate fields that are stored in backend
    // dateOfBirth and state will come from quoteData, NOT from getApplication
    const contactInformation = {
        applicantType,
        firstName: response.firstName || "",
        lastName: response.lastName || "",
        // DOB intentionally omitted - comes from quoteData
        email: response.email || "",
        phone: response.phone ? formatPhone(response.phone) : "",
        phoneType: (response.phoneType as "Home" | "Mobile" | "Business") || "",
        tcpaConsent: response.tcpaConsent,
        address: response.addressline1 || "",
        city: response.city || "",
        // state intentionally omitted - comes from quoteData
        zipCode: response.zipCd || "",
        gender: response.gender ? toLowerCaseGenderFromSingleLetter(response.gender) : undefined,
        aaaMemberNumber: response.aaaMembershipNumber || "",
    } as any; // Type assertion needed since we're intentionally omitting required fields

    // Transform statement of health
    // Note: hasUsedNicotine intentionally omitted - comes from quoteData
    const statementOfHealth = {
        heightFeet: response.heightFeet || "",
        heightInches: response.heightInches || "",
        weight: response.weight || "",
        hasMedicalConditions: response.hasMedicalConditions ?? null,
        hasDiagnosticTesting: response.hasDiagnosticTesting ?? null,
    } as any; // Type assertion needed since we're intentionally omitting hasUsedNicotine

    // Transform coverage replacement
    const coverageReplacement = {
        replacementIndicator: response.replacementIndicator ?? null,
        replacementCompany: response.replacementCompany || "",
        replacementAccount: response.replacementAccount || "",
    };

    // Transform policy beneficiary
    const policyBeneficiary = {
        firstName: response.beneFirstName || "",
        lastName: response.beneLastName || "",
        relationship: response.beneRelationship || "",
    } as any; // Type assertion needed since we're allowing empty strings for enum fields

    // Transform secondary addressee
    const secondaryAddressee = {
        hasSecondaryAddressee: response.hasSecondaryAddressee ?? null,
        firstName: response.secondaryFirstName || "",
        lastName: response.secondaryLastName || "",
        address: response.secondaryAddress || "",
        city: response.secondaryCity || "",
        state: response.secondaryState || "",
        zipCode: response.secondaryZipCd || "",
        phone: response.secondaryPhone ? formatPhone(response.secondaryPhone) : "",
    } as any; // Type assertion needed since we're allowing empty strings for enum fields

    return {
        contactInformation,
        statementOfHealth,
        coverageReplacement,
        policyBeneficiary,
        secondaryAddressee,
        policyNumber: response.policyNumber ?? null,
        hannoverId: response.hannoverId ?? null,
        clubCode: null, // clubCode is not stored in backend, will be fetched from CDS
    };
};
